import {
  BaseMutationOptions,
  ResolverMutationRequestMetadata,
  parseResolverResponse,
  CreateUserRequest,
  CreateUserClientData,
  UpdateUserRequest,
  UpdateUserClientData,
  SoftDeleteUserRequest,
  SoftDeleteUserClientData,
  RestoreUserRequest,
  RestoreUserClientData
} from '@esg/syntiro-api-core';
import { User } from '@esg/esg-global-types';
import { FetchResult } from '@apollo/client';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import { FUNCTION_NO_DATA_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { auth } from '../../lib/google/firebase';
import { validateParams } from '../validators/base.validator';
import { apollo_client_v2 } from '../../App';
import {
  CREATE_USER,
  UPDATE_USER,
  SOFT_DELETE_USER,
  RESTORE_USER,
  UPDATE_USER_LAST_ACCESSED,
  SOFT_DELETE_USERS,
  RESTORE_USERS,
  UPDATE_USERS_ACCESS,
  CREATE_USERS
} from '../mutations/user.mutations';
import { GET_USERS_PAGE, CHECK_EXISTING_EMAILS } from '../queries/user.queries';
import { stripTypenames } from '../utilities/strip_typenames.util';
import {
  isValidUserEmail,
  isValidUserGroups,
  isValidUserId,
  isValidUserName,
  isValidUserPhoneNumber,
  isValidUserType
} from '../validators/user.validator';

const SOURCE = 'user.service.ts';

/**
 * Create a user in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request.
 * @param {CreateUserClientData} data Required data to populate user fields.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<User | null | undefined>}
 */
export const createUser = async (
  metadata: ResolverMutationRequestMetadata,
  data: CreateUserClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<User | null | undefined> => {
  // Normalise before validating, exactly as updateUser does. `isValidUserEmail`
  // is anchored and allows no surrounding whitespace, so validating the raw
  // value rejects a pasted address as malformed instead of trimming it. Guarded
  // on the type so a null/non-string email still fails validation rather than
  // throwing on `.trim()`. Inlined rather than using `normaliseUserEmail`:
  // `shared/email_validation` imports `checkExistingEmails` from this module,
  // so importing it back would close a cycle.
  const request: CreateUserClientData =
    typeof data.email === 'string' ? { ...data, email: data.email.trim().toLowerCase() } : data;

  validateParams(
    { data: request },
    {
      'data.id': {
        validate: isValidUserId,
        message: 'Invalid user id specified.'
      },
      'data.first_name': {
        validate: isValidUserName,
        message: 'Invalid first_name specified.'
      },
      'data.last_name': {
        validate: isValidUserName,
        message: 'Invalid last_name specified.'
      },
      'data.email': {
        validate: isValidUserEmail,
        message: 'Invalid email specified.'
      },
      'data.phone': {
        validate: isValidUserPhoneNumber,
        message: 'Invalid phone number specified.'
      },
      'data.type': {
        validate: isValidUserType,
        message: 'Invalid type specified.'
      }
    }
  );
  !data.super_admin &&
    validateParams(
      { data },
      {
        'data.groups': {
          validate: isValidUserGroups,
          message: 'Invalid groups specified.'
        }
      }
    );
  const input: CreateUserRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      user: request
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: CREATE_USER,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.createUser) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('createUser'));
      }
      return parseResolverResponse<User>(response.data.createUser);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createUser'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Update a user in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request.
 * @param {UpdateUserClientData} data Required data to update user fields.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<User | null | undefined>}
 */
export const updateUser = async (
  metadata: ResolverMutationRequestMetadata,
  data: UpdateUserClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<User | null | undefined> => {
  // Normalise before validating, mirroring the API. `isValidUserEmail` is
  // anchored and allows no surrounding whitespace, so validating the raw value
  // rejects a pasted address as malformed instead of simply trimming it — and
  // it does so client-side, before the API's own normalisation can run.
  const request: UpdateUserClientData =
    data.email === undefined ? data : { ...data, email: data.email.trim().toLowerCase() };

  validateParams(
    { metadata, data: request },
    {
      'data.id': {
        validate: isValidUserId,
        message: 'Invalid user id specified.'
      },
      'data.email': {
        validate: isValidUserEmail,
        message: 'Invalid email specified.',
        is_optional: true
      }
    }
  );
  const input: UpdateUserRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      user: stripTypenames(request)
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: UPDATE_USER,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.updateUser) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('updateUser'));
      }
      return parseResolverResponse<User>(response.data.updateUser);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateUser'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Soft delete a user in database.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request.
 * @param {SoftDeleteUserClientData} data Contains the ID of the user to soft delete.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<User | null | undefined>}
 */
export const softDeleteUser = async (
  metadata: ResolverMutationRequestMetadata,
  data: SoftDeleteUserClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<User | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'data.id': {
        validate: isValidUserId,
        message: 'Invalid user id specified.'
      }
    }
  );
  const input: SoftDeleteUserRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      user: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: SOFT_DELETE_USER,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.softDeleteUser) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('softDeleteUser'));
      }
      return parseResolverResponse<User>(response.data.softDeleteUser);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'softDeleteUser'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Restore a soft-deleted user in database.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request.
 * @param {RestoreUserClientData} data Contains the ID of the user to restore.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<User | null | undefined>}
 */
export const restoreUser = async (
  metadata: ResolverMutationRequestMetadata,
  data: RestoreUserClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<User | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'data.id': {
        validate: isValidUserId,
        message: 'Invalid user id specified.'
      }
    }
  );
  const input: RestoreUserRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      user: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: RESTORE_USER,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.restoreUser) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('restoreUser'));
      }
      return parseResolverResponse<User>(response.data.restoreUser);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'restoreUser'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Update a user's last accessed field in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request.
 * @param {UpdateUserClientData} data Contains the ID of the user that needs to be updated.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<User | null | undefined>}
 */
export const updateUserLastAccessed = async (
  metadata: ResolverMutationRequestMetadata,
  data: UpdateUserClientData,
  options: BaseMutationOptions = { return_data: true, create_audit_log: false }
): Promise<string | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'data.id': {
        validate: isValidUserId,
        message: 'Invalid user id specified.'
      }
    }
  );

  const input: UpdateUserRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      user: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: UPDATE_USER_LAST_ACCESSED,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.updateUserLastAccessed) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('updateUserLastAccessed'));
      }
      return parseResolverResponse<string>(response.data.updateUserLastAccessed);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateUserLastAccessed'),
        { metadata, data },
        tracking_id
      );
    });
};

/** Local mirror of the backend bulk-mutation envelope shape. */
export interface BulkUserMutationEnvelope {
  successes: Array<User>;
  failures: Array<{ id: string; error: string }>;
  skipped: Array<{ id: string; reason: string }>;
}

export interface GetUsersPageFilters {
  search?: string;
  role?: string;
  company_id?: string;
  group_id?: string;
  invitation_status?: 'pending' | 'accepted';
  type?: User['type'];
  include_deleted?: boolean;
  /** Derived list-view status; not Firestore-indexed, applied server-side in memory. */
  status?: 'active' | 'not_invited' | 'invited' | 'invite_expired' | 'deleted';
  /** True to match users who administer at least one group; not Firestore-indexed. */
  is_group_admin?: boolean;
  /** True/false to match on the super_admin flag; applied server-side as a residual truthy check (some docs store it as the string "true"), not a strict Firestore equality filter. */
  super_admin?: boolean;
}

interface GetUsersPageInput {
  metadata: { tracking_id?: string };
  filter?: GetUsersPageFilters;
  cursor?: string | null;
  limit?: number;
}

/** Paginated user list — returns the first page plus a `nextCursor`. */
export const getUsersPage = async (
  filter?: GetUsersPageFilters,
  cursor?: string | null,
  limit?: number
): Promise<{ users: Array<User>; nextCursor: string | null }> => {
  const input: GetUsersPageInput = {
    metadata: { tracking_id: generateUuidV4() },
    filter,
    cursor: cursor ?? undefined,
    limit
  };
  return apollo_client_v2
    .query({
      query: GET_USERS_PAGE,
      variables: { input },
      fetchPolicy: 'no-cache'
    })
    .then((response) => {
      if (!response.data?.getUsersPage) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('getUsersPage'));
      }
      const parsed = parseResolverResponse<{
        users: Array<User>;
        nextCursor: string | null;
      }>(response.data.getUsersPage);
      return parsed ?? { users: [], nextCursor: null };
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getUsersPage'),
        { filter, cursor, limit },
        tracking_id
      );
    });
};

/**
 * Returns which of the supplied emails already belong to a user
 * (case-insensitive, including soft-deleted users). Backs the Add User and CSV
 * import duplicate checks so they don't depend on the client having the full
 * user list loaded. Returns a lower-cased set for direct membership testing.
 * @param {string[]} emails Candidate emails to test.
 * @returns {Promise<Set<string>>} Lower-cased emails that already exist.
 */
export async function checkExistingEmails(emails: string[]): Promise<Set<string>> {
  if (emails.length === 0) return new Set();
  return apollo_client_v2
    .query({
      query: CHECK_EXISTING_EMAILS,
      variables: { input: { metadata: { tracking_id: generateUuidV4() }, emails } },
      fetchPolicy: 'no-cache'
    })
    .then((response) => {
      if (!response.data?.checkExistingEmails) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('checkExistingEmails'));
      }
      const parsed = parseResolverResponse<string[]>(response.data.checkExistingEmails);
      return new Set((parsed ?? []).map((email) => email.toLowerCase()));
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'checkExistingEmails'),
        { emailCount: emails.length },
        tracking_id
      );
    });
}

const emptyEnvelope = (): BulkUserMutationEnvelope => ({
  successes: [],
  failures: [],
  skipped: []
});

const runBulk = async (
  metadata: ResolverMutationRequestMetadata,
  mutation: typeof SOFT_DELETE_USERS,
  variables: Record<string, unknown>,
  resolver_key: 'softDeleteUsers' | 'restoreUsers' | 'updateUsersAccess' | 'createUsers'
): Promise<BulkUserMutationEnvelope> => {
  const input = {
    metadata: { ...metadata, audit_user_email: auth.currentUser?.email ?? '' },
    ...variables,
    options: { return_data: true, create_audit_log: true }
  };
  return apollo_client_v2
    .mutate({ mutation, variables: { input } })
    .then((response: FetchResult) => {
      const payload = response.data?.[resolver_key];
      if (!payload) throw new Error(FUNCTION_NO_DATA_FAILURE(resolver_key));
      return parseResolverResponse<BulkUserMutationEnvelope>(payload) ?? emptyEnvelope();
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, resolver_key),
        { metadata, variables },
        tracking_id
      );
    });
};

/** Soft-deletes a batch of users (≤200 per call). */
export const softDeleteUsers = async (
  metadata: ResolverMutationRequestMetadata,
  ids: Array<string>
): Promise<BulkUserMutationEnvelope> =>
  runBulk(
    metadata,
    SOFT_DELETE_USERS,
    { data: { users: ids.map((id) => ({ id })) } },
    'softDeleteUsers'
  );

/** Restores a batch of soft-deleted users. */
export const restoreUsers = async (
  metadata: ResolverMutationRequestMetadata,
  ids: Array<string>
): Promise<BulkUserMutationEnvelope> =>
  runBulk(metadata, RESTORE_USERS, { data: { users: ids.map((id) => ({ id })) } }, 'restoreUsers');

/** Grants a (group, company, role) to a batch of users. */
export const updateUsersAccess = async (
  metadata: ResolverMutationRequestMetadata,
  args: {
    ids: Array<string>;
    target_group_id: string;
    target_company_id: string;
    role: string;
  }
): Promise<BulkUserMutationEnvelope> =>
  runBulk(metadata, UPDATE_USERS_ACCESS, { data: { users: args } }, 'updateUsersAccess');

/** Bulk-creates users; rows with an existing email return in `skipped`. */
export interface CreateUsersRow {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  super_admin: boolean;
  type: 'internal' | 'external' | 'any';
  groups: Record<string, unknown>;
}

export const createUsers = async (
  metadata: ResolverMutationRequestMetadata,
  rows: Array<CreateUsersRow>
): Promise<BulkUserMutationEnvelope> =>
  runBulk(metadata, CREATE_USERS, { data: { users: rows } }, 'createUsers');
